import { Clock4, Loader2, Plus, Search, ShieldCheck, Sparkles, Users } from "lucide-react";
import { Alert, AlertTitle, Button, Input, Progress, useToast } from "randmarcomps";
import * as React from "react";
import { Link } from "react-router-dom";
import { OpportunityEntry, useGetV4ManufacturerByRouteManufacturerIdOpportunitiesQuery } from "randmar-api-client";
import OpportunitiesGrid from "../components/grids/OpportunitiesGrid";
import CreateNewOpportunitiesSteps from "../components/opportunities/CreateNewOpportunitiesSteps";
import { DocumentHeader } from "../components/util/StyledComponents";
import { useManufacturer } from "../contexts/WithManufacturer";
import Path from "../routes/Path";
import withRoot from "../withRoot";

type FilterKey = "active" | "defaultStatus" | "defaultResellers" | "expiringSoon";

const defaultFiltersState: Record<FilterKey, boolean> = {
  active: true,
  defaultStatus: false,
  defaultResellers: false,
  expiringSoon: false,
};

const filterLabelMap: Record<FilterKey, string> = {
  active: "Active",
  defaultStatus: "Default status",
  defaultResellers: "Default resellers",
  expiringSoon: "Expiring soon",
};

const parseDateOrNull = (dateValue?: string | null) => {
  if (!dateValue) return null;
  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) return null;
  return parsedDate;
};

const hasEndDateInFuture = (endDate?: string | null) => {
  const parsedEndDate = parseDateOrNull(endDate);
  if (!parsedEndDate) return false;

  const now = new Date();
  return parsedEndDate >= now;
};

const isOpportunityActive = (opportunity: OpportunityEntry) =>
  Boolean(opportunity.Active) && hasEndDateInFuture(opportunity.EndDate);

const isOpportunityExpiringSoon = (endDate?: string | null) => {
  const parsedEndDate = parseDateOrNull(endDate);
  if (!parsedEndDate) return false;

  const now = new Date();
  const threshold = new Date(now.getTime());
  threshold.setDate(threshold.getDate() + 30);

  return parsedEndDate >= now && parsedEndDate <= threshold;
};

const matchesSearchTerm = (opportunity: OpportunityEntry, normalizedSearch: string) => {
  if (!normalizedSearch) return true;

  return [
    opportunity.BidNumber,
    opportunity.Opportunity,
    opportunity.Description,
    opportunity.DefaultResellers,
  ].some(value => (value || "").toLowerCase().includes(normalizedSearch));
};

const Opportunities: React.FC = () => {
  const { applicationId } = useManufacturer();
  const { toast } = useToast();
  const { data: opportunitiesData, isLoading: loading, isError } = useGetV4ManufacturerByRouteManufacturerIdOpportunitiesQuery({ routeManufacturerId: applicationId });

  const opportunities: OpportunityEntry[] = React.useMemo(
    () => (Array.isArray(opportunitiesData) ? (opportunitiesData as OpportunityEntry[]) : []),
    [opportunitiesData]
  );
  const [searchTerm, setSearchTerm] = React.useState("");
  const [filters, setFilters] = React.useState<Record<FilterKey, boolean>>(() => ({ ...defaultFiltersState }));
  const [showFilterSpinner, setShowFilterSpinner] = React.useState(false);

  React.useEffect(() => {
    if (isError) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "There was a problem fetching opportunities.",
      });
    }
  }, [isError, toast]);

  const stats = React.useMemo(() => {
    const activeOpportunities = opportunities.filter(isOpportunityActive);
    const activeDefaultResellers = activeOpportunities.filter(opportunity => Boolean(opportunity.DefaultResellers));
    const activeChosenResellers = Math.max(activeOpportunities.length - activeDefaultResellers.length, 0);

    return [
      { label: "Total active opportunities", value: activeOpportunities.length.toString() },
      { label: "Active default resellers opportunities", value: activeDefaultResellers.length.toString() },
      { label: "Active chosen reseller opportunities", value: activeChosenResellers.toString() },
    ];
  }, [opportunities]);

  const filterOptions: { id: FilterKey; label: string; icon: React.ReactNode }[] = [
    { id: "active", label: filterLabelMap.active, icon: <Sparkles className="h-4 w-4" /> },
    { id: "defaultStatus", label: filterLabelMap.defaultStatus, icon: <ShieldCheck className="h-4 w-4" /> },
    { id: "defaultResellers", label: filterLabelMap.defaultResellers, icon: <Users className="h-4 w-4" /> },
    { id: "expiringSoon", label: filterLabelMap.expiringSoon, icon: <Clock4 className="h-4 w-4" /> },
  ];

  const normalizedSearch = React.useMemo(() => searchTerm.trim().toLowerCase(), [searchTerm]);
  const isUsingDefaultFilters = React.useMemo(
    () =>
      filters.active === defaultFiltersState.active &&
      filters.defaultStatus === defaultFiltersState.defaultStatus &&
      filters.defaultResellers === defaultFiltersState.defaultResellers &&
      filters.expiringSoon === defaultFiltersState.expiringSoon,
    [filters]
  );

  const opportunitiesMatchingFilters = React.useMemo(() => {
    return opportunities.filter(opportunity => {
      return (
        (!filters.active || isOpportunityActive(opportunity)) &&
        (!filters.defaultStatus || Boolean(opportunity.CanBeSetAsDefault)) &&
        (!filters.defaultResellers || Boolean(opportunity.DefaultResellers)) &&
        (!filters.expiringSoon || isOpportunityExpiringSoon(opportunity.EndDate))
      );
    });
  }, [opportunities, filters]);

  const filteredOpportunities = React.useMemo(() => {
    if (!normalizedSearch) return opportunitiesMatchingFilters;
    return opportunitiesMatchingFilters.filter(opportunity => matchesSearchTerm(opportunity, normalizedSearch));
  }, [opportunitiesMatchingFilters, normalizedSearch]);

  const selectedFilterLabels = React.useMemo(() => {
    return (Object.keys(filters) as FilterKey[])
      .filter(filterKey => filters[filterKey])
      .map(filterKey => filterLabelMap[filterKey].toLowerCase());
  }, [filters]);

  const filterSummaryLabel = React.useMemo(() => {
    if (selectedFilterLabels.length === 0) return "opportunities";
    return `${selectedFilterLabels.join(" & ")} opportunities`;
  }, [selectedFilterLabels]);

  const showResultsSummary = React.useMemo(
    () => opportunities.length > 0 && (Boolean(normalizedSearch) || !isUsingDefaultFilters),
    [opportunities.length, normalizedSearch, isUsingDefaultFilters]
  );

  React.useEffect(() => {
    if (loading) {
      setShowFilterSpinner(false);
      return;
    }
    if (!normalizedSearch && isUsingDefaultFilters) {
      setShowFilterSpinner(false);
      return;
    }

    setShowFilterSpinner(true);
    const timer = setTimeout(() => setShowFilterSpinner(false), 400);
    return () => clearTimeout(timer);
  }, [normalizedSearch, isUsingDefaultFilters, loading]);

  const handleToggleFilter = React.useCallback((filterId: FilterKey) => {
    setFilters(prev => ({ ...prev, [filterId]: !prev[filterId] }));
  }, []);

  const handleResetFilters = React.useCallback(() => {
    setSearchTerm("");
    setFilters({ ...defaultFiltersState });
  }, []);

  return (
    <>
      {loading && <Progress className="mb-4" />}

      <DocumentHeader
        sticky={false}
        justifyContent="space-between"
        alignItems="stretch"
        direction="column"
        className="gap-6 border border-[#d8c8f5] bg-gradient-to-r from-[#8E77C8]/20 via-[#f7f2ff] to-white"
      >
        <div className="flex flex-col gap-4">
          <div className="categories-accent-text flex flex-wrap items-center gap-2 text-sm font-medium uppercase tracking-wide">
            <span className="categories-accent-pill inline-flex items-center gap-2 rounded-full px-3 py-1">
              <Sparkles className="h-4 w-4" />
              Manufacturer Portal
            </span>
            {loading && (
              <span className="inline-flex items-center gap-1 categories-accent-text">
                <Loader2 className="h-4 w-4 animate-spin" />
                Syncing
              </span>
            )}
          </div>
          <div>
            <h1 className="text-3xl font-semibold text-slate-900">Opportunities</h1>
            <p className="mt-1 text-base text-slate-600">
              Choose resellers to be targeted by specific rebates. Qualify a reseller and add them to as many opportunities as you wish.
            </p>
          </div>
          <div className="grid gap-3 text-sm sm:grid-cols-3">
            {stats.map(stat => (
              <div
                key={stat.label}
                className="categories-stat-card rounded-xl border bg-white/80 px-4 py-3 shadow-sm"
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{stat.label}</p>
                <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-end">
          <Link to={Path.createOpportunity(applicationId)} className="self-start md:self-auto">
            <Button size="lg" className="categories-accent-pill--primary border-0 shadow-lg">
              <Plus className="mr-2 h-4 w-4" />
              New Opportunity
            </Button>
          </Link>
        </div>
      </DocumentHeader>

      <section className="mb-6 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search by name, bid number, or default reseller tier"
              className="w-full rounded-xl border-slate-200 bg-slate-50 pl-9 text-sm"
              aria-label="Search opportunities"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold uppercase tracking-wide text-slate-500 hover:text-[color:var(--ops-accent)]"
              >
                Clear
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {filterOptions.map(option => {
              const isActive = filters[option.id];
              return (
                <button
                  type="button"
                  key={option.id}
                  aria-pressed={isActive}
                  onClick={() => handleToggleFilter(option.id)}
                  className={`categories-filter-button inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium ${
                    isActive ? "categories-filter-button--active" : ""
                  }`}
                >
                  {option.icon}
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
        {showResultsSummary && (
          <p className="mt-2 text-sm text-slate-600">
            Showing {filteredOpportunities.length.toLocaleString()} of {opportunitiesMatchingFilters.length.toLocaleString()} {filterSummaryLabel}.
          </p>
        )}
        {showFilterSpinner && (
          <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-[color:var(--ops-accent)]" />
            Updating results…
          </div>
        )}
      </section>

      {loading && opportunities.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white/70 p-6 text-center text-slate-500 shadow-sm">
          Fetching opportunities…
        </div>
      ) : opportunities.length === 0 ? (
        <CreateNewOpportunitiesSteps />
      ) : filteredOpportunities.length === 0 ? (
        <NoOpportunitiesResults onReset={handleResetFilters} />
      ) : (
        <OpportunitiesGrid opportunities={filteredOpportunities} />
      )}
    </>
  );
}

const NoOpportunitiesResults: React.FC<{ onReset: () => void }> = ({ onReset }) => (
  <Alert variant="info" className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
    <div>
      <AlertTitle>No opportunities match these filters</AlertTitle>
      <p className="text-sm text-slate-600">Update your filters or clear search to see every opportunity again.</p>
    </div>
    <Button onClick={onReset} variant="outline" className="self-start md:self-auto">
      Reset filters
    </Button>
  </Alert>
);

export default withRoot(Opportunities);
